import { Feature } from "src/states/ApplicationStateFeatures"
import {
  AppMessagesContext, Localization,
  SessionContext, SettingsFixedPriorities,
  PersistenceScope
} from "@viamap/viamap2-common"
import { Fragment, useContext, useEffect, useState } from "react"
import { ApplicationStateContext } from "src/states/ApplicationState"
import { ADModalBody, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal"
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput"
import { ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState"
import { useWindowContext } from "src/WindowManager/useWindowContext"
import { MIBIcon } from "src/componentsUtils/MIBIcon"
import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react"
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers"

type SingleTranslation = {
  da: string,
  en: string
}

type MinMax = {
  min?: number,
  max?: number
}

export type GroupInfo = ({
  key: string,
} & SingleTranslation)[]

type SettingsType =
  | { type: "String" }
  | { type: "TextArea" }
  | { type: "Select", options: ({ value: any } & SingleTranslation)[] }
  | { type: "Integer", suffix?: string } & MinMax
  | { type: "Float", precision?: number, suffix?: string } & MinMax
  /**
   * Percent stores its underlying value as a 0..1 fraction (so existing consumers keep working) but
   * presents it to the user multiplied by 100. `min`/`max`/`precision` are interpreted in the
   * displayed (0..100) space. Use `suffix: "%"` to render a percent sign next to the input.
   */
  | { type: "Percent", precision?: number, suffix?: string } & MinMax
  | { type: "Boolean", true: SingleTranslation, false: SingleTranslation }
  | { type: "TimeADay" }
  | { type: "Date" } & MinMax
  | { type: "DateTime" } & MinMax
  | { type: "Year" } & MinMax
  | { type: "ExternalEditor", windowId: WindowId, default?: any }

export type SingleSetting = {
  access: Feature | Feature[],
  scope?: PersistenceScope,
  default?: boolean | string | number | any[],
  key: string,
  group: string,
  /** Optional explanatory text shown as an info-icon hover-popup next to the label. */
  tooltip?: SingleTranslation,
} & SettingsType & SingleTranslation

export type keyValueObject = { [key: string]: any }

export interface SettingsManagerInterface {
  extractSettingsByPriority: (source: SettingsFixedPriorities, priority: SettingsFixedPriorities, includeDisabled: boolean) => keyValueObject
  clearByPriority: (source: SettingsFixedPriorities) => void
  addSettings: (settings: keyValueObject, { filename, priority }: { filename: string, priority: SettingsFixedPriorities }) => void
  getSystemSetting: (key: string, defaultValue: any, includeDisabled: boolean) => any
}

export interface SettingsManagerInterface {
  extractSettingsByPriority: (source: SettingsFixedPriorities, priority: SettingsFixedPriorities, includeDisabled: boolean) => keyValueObject
  clearByPriority: (source: SettingsFixedPriorities) => void
  addSettings: (settings: keyValueObject, { filename, priority }: { filename: string, priority: SettingsFixedPriorities }) => void
  getState: () => any
  getSystemSetting: (key: string, defaultValue: any, includeDisabled: boolean) => any
}

export function SettingsEditor(props: {
  managedWindow?: ManagedWindow,
  settingsManager: SettingsManagerInterface,
  scope: PersistenceScope,
  priority: SettingsFixedPriorities,
  filteredSettingsGroup: GroupInfo[number][],
  allSettings: SingleSetting[],
  affectedMessageText: string,
  getSource: (key: string) => number
  callbackOnClose: () => any,
  callbackToResetCatchment: (a: any) => any
  callbackOnSaveChanges: (localSettings, sessionState, onClose, appMessageDispatch, source: SettingsFixedPriorities) => any
}) {
  const [settingValues, setSettingValue] = useState<{ [key: string]: { value: any, disabled: boolean, reset: boolean } }>({})
  const { state: sessionState } = useContext(SessionContext);
  const { dispatch: appMessageDispach } = useContext(AppMessagesContext);

  useEffect(() => {
    let priority = props.priority;
    let settingValues = { ...props.settingsManager.extractSettingsByPriority(priority, priority, true) };
    let handleNested = handleNestedSettings(props.allSettings.map((a) => a.key), settingValues);
    Object.keys(handleNested).forEach((key) => {
      handleNested[key] = { value: handleNested[key], disabled: false, reset: false }
    })
    setSettingValue(() => ({ ...(handleNested) }))
  }, [props.scope])

  function savePersistentSettings() {
    onClickSaveCustomUserSettings(settingValues, sessionState, props.callbackOnClose, appMessageDispach, props.priority)
  }



  function setSetting(key, value) {
    let newSettings = { ...settingValues, [key]: value };
    if (value.value && typeof value.value === "object" && !Array.isArray(value.value) && !(value.value instanceof Date)) {
      newSettings = { ...settingValues }
      delete newSettings[key]
      Object.keys(value.value).forEach((k) => {
        newSettings = { ...newSettings, [key + "." + k]: { value: value.value[k], disabled: value.disabled, reset: value.reset } }
      })
    }

    const source = props.priority;
    const settings = removeUndefined(newSettings)
    props.settingsManager.clearByPriority(source);
    props.settingsManager.addSettings(settings, { filename: "LiveSetting", priority: source });
    let settingValuesExtracted = { ...props.settingsManager.extractSettingsByPriority(source, source, true) };
    let handleNested = handleNestedSettings(props.allSettings.map((a) => a.key), settingValuesExtracted);
    Object.keys(handleNested).forEach((key) => {
      handleNested[key] = { value: handleNested[key], disabled: false, reset: false }
    })
    setSettingValue(() => ({ ...(handleNested) }))
  }

  function closeWindow() {
    savePersistentSettings()
    props.callbackOnClose()
  }

  async function onClickSaveCustomUserSettings(localSettings, sessionState, onClose, appMessageDispatch, source: SettingsFixedPriorities): Promise<void> {
    let settings = removeUndefined(localSettings);
    return props.callbackOnSaveChanges(flattenSettings(settings), sessionState, onClose, appMessageDispatch, source);
  }

  let filteredSettingsGroup = props.filteredSettingsGroup;

  return (
    <AdvancedDragModal
      window={props.managedWindow}
      variant={"NSWhite"}
      title={Localization.getText("Settings")}
      PosDefault={{ left: "50%", top: "5%", width: "800px", transform: "translateX(-50%)" }}
      onClose={closeWindow}
    >
      <ADModalInfo>
        {props.affectedMessageText}
      </ADModalInfo>
      <ADModalBody >

        <GlassInputGroup
          autoFocus={0}
          onSubmit={savePersistentSettings}
          onEscape={closeWindow}
        >
          {(filteredSettingsGroup).map((a) => {
            return <Fragment key={a.key}><Group settingsManager={props.settingsManager} scope={props.scope} group={a} values={settingValues} allSettings={props.allSettings} getSource={props.getSource} onChange={(key, value) => setSetting(key, value)} /></Fragment>
          })}
        </GlassInputGroup>
      </ADModalBody>
    </AdvancedDragModal>
  )
}

function Group(props: { settingsManager: SettingsManagerInterface, scope: PersistenceScope, group: GroupInfo[number], values: keyValueObject, allSettings: SingleSetting[], getSource: (key: string) => number, onChange: (key, value) => any }) {
  const { hasAccessToFeature } = useContext(ApplicationStateContext)
  const availableSettings = props.allSettings.filter((ss) => {
    return ss.group == props.group.key && (typeof ss.access == "string" ? hasAccessToFeature(ss.access) : ss.access.every(() => hasAccessToFeature))
  })
  if (availableSettings.length === 0) {
    return
  }
  return (
    <>
      <div className="GlassInputGroupTitle">{props.group[Localization.getLanguage()]}</div>
      {availableSettings.map((ss) => {
        return <Fragment key={ss.key}><SettingRow settingsManager={props.settingsManager} scope={props.scope} setting={ss} values={props.values} getSource={props.getSource} onChange={props.onChange} /></Fragment>
      })}
    </>
  )
}

function SettingLabelWithTooltip(props: { label: string, tooltip: string }) {
  const { root } = useFloatingPortalContext();
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open,
    middleware: [
      offset(5),
      ...(root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })])
    ],
    onOpenChange: setOpen,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context),
    useDismiss(context)
  ]);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      <span>{props.label}</span>
      <span
        ref={refs.setReference}
        style={{ cursor: "help", lineHeight: 1, fontSize: "12px", color: "#666", display: "inline-flex" }}
        {...getReferenceProps()}
      >
        <MIBIcon icon="info" />
      </span>
      {Boolean(open) && (
        <MITFloatingPortal>
          <div
            ref={refs.setFloating}
            className="ViewButtonToolTip assumptions-box"
            style={{
              ...floatingStyles,
              maxWidth: "320px",
              fontSize: "0.85em",
              zIndex: 10000
            }}
            {...getFloatingProps()}
          >
            <div>
              <div style={{ fontWeight: "bold", marginBottom: "4px" }}>{props.label}</div>
              <div style={{ fontSize: "0.95em", lineHeight: "1.4" }}>{props.tooltip}</div>
            </div>
          </div>
        </MITFloatingPortal>
      )}
    </span>
  );
}

function IconBySource(props: { value: SettingsFixedPriorities }) {
  let value = props.value
  switch (value) {
    case (0 as SettingsFixedPriorities):
    case SettingsFixedPriorities.GlobalSettings:
    case SettingsFixedPriorities.EnvironmentSettings:
    case SettingsFixedPriorities.ProductVariantSettings:
      return <div className="SourceIcon" title={Localization.getText("Default")}><MIBIcon icon="global" /></div>
    case SettingsFixedPriorities.LicenseSettings:
      return <div className="SourceIcon" title={Localization.getText("License")}><MIBIcon icon="license" /></div>
    case SettingsFixedPriorities.CustomerSettings:
      return <div className="SourceIcon" title={Localization.getText("Customer")}><MIBIcon icon="customer" /></div>
    case SettingsFixedPriorities.UserSettings:
      return <div className="SourceIcon" title={Localization.getText("User")}><MIBIcon icon="user" /></div>
    case 70 as SettingsFixedPriorities:
      return <div className="SourceIcon" title={Localization.getText("Project")}><MIBIcon icon="project" /></div>
    default:
      return <div className="SourceIcon" title={Localization.getText("Unknown")}><></></div>
  }
}

function SettingRow(props: { settingsManager: SettingsManagerInterface, scope: PersistenceScope, setting: SingleSetting, values: keyValueObject, getSource: (key: string) => number, onChange: (key, value) => any }) {
  let x = props.values[props.setting.key]

  const isNested = x?.value && typeof x.value === "object" && !Array.isArray(x.value)
  const value = x?.value ?? props.settingsManager.getSystemSetting(props.setting.key, props.setting.default, true)
  const wrongScope = (props.setting.scope !== undefined && props.scope !== props.setting.scope);
  const disabled = wrongScope || (x?.disabled ?? true)
  // const disabled = wrongScope || (props.values[props.setting.key]?.disabled ?? true)
  const lang: "da" | "en" = Localization.getLanguage() as "da" | "en";
  const tooltipText = props.setting.tooltip?.[lang];
  const labelNode = tooltipText
    ? <SettingLabelWithTooltip label={props.setting[lang]} tooltip={tooltipText} />
    : props.setting[lang];
  return <>
    <GlassTextInputTransformer label={labelNode}>
      <GlassInputControl>
        <IconBySource value={props.getSource(isNested ? (props.setting.key + "." + Object.keys(x.value)?.[0]) : props.setting.key)} />
        {props.values[props.setting.key]?.reset ?
          <span className={"disabled"}>{Localization.getText("Reset to default")}</span>
          : <>
            <SettingsInput disabled={disabled} value={value} setting={props.setting} onChange={(value) => { props.onChange(props.setting.key, { value: stringToValue(props.setting, value), disabled: disabled, reset: false }) }} />
            <span className={(disabled ? "disabled" : "")}>{"suffix" in props.setting ? props.setting.suffix : ""}</span>
          </>}
      </GlassInputControl>
      <div className="Setting_Butto" style={{ color: "grey" }}>
        {wrongScope ? <span title={Localization.getText("Editing setting is disabled")}>
          <MIBIcon icon="disallowed" />
        </span> : disabled ?
          <span title={Localization.getText("Edit")} className="sm" onClick={() => { props.onChange(props.setting.key, { value: value, disabled: false, reset: false }) }}>
            <MIBIcon icon="edit" />
          </span> :
          <span onClick={() => { props.onChange(props.setting.key, { value: value, disabled: true, reset: true }) }} title={Localization.getText("Reset")} >
            <MIBIcon icon="reset" />
          </span>
        }
      </div>
    </GlassTextInputTransformer>
  </>
}

function stringToValue(setting: SingleSetting, value) {
  if (value === undefined) {
    return undefined
  }
  switch (setting.type) {
    case "ExternalEditor": return value;
    case "Boolean": return value === "true" ? true : false
    case "TimeADay": {
      let split = value.split(":")
      return parseInt(split[0]) * 3600 + parseInt(split[1]) * 60
    }
    // case "DateTime": 
    case "Date": return parseInt(new Date(value).toISOString().split("T")[0].replaceAll("-", ""))
    case "Year": //next
    case "Integer": {
      let valueInt = value !== "" ? parseInt(value) : (setting.min ?? 0);
      return Math.max(Math.min(setting.max ?? valueInt, valueInt), setting.min ?? valueInt)
    }
    case "Float": return parseFloat(value)
    case "Percent": {
      // The input lives in the displayed (0..100) space; clamp there, then convert to a 0..1 fraction.
      const raw = value === "" ? (setting.min ?? 0) : parseFloat(value);
      if (Number.isNaN(raw)) {
        return undefined;
      }
      const clamped = Math.max(Math.min(setting.max ?? raw, raw), setting.min ?? raw);
      return clamped / 100;
    }
    default: return value?.toString() || value
  }
}

function valueToString(setting: SingleSetting, value) {
  if (value === undefined) {
    return ""
  }
  switch (setting.type) {
    case "ExternalEditor": return value;
    case "TimeADay": return `${format2Digits(Math.floor(value / 3600))}:${format2Digits(Math.floor(value / 60) % 60)}`
    case "Integer": return value === "" ? (setting.min ?? "0") : value;
    case "Date": {
      let copy = ((value?.toString() || "").length == 8 ? value.toString() : "00000000").split("");
      return `${copy.splice(0, 4).join("")}-${copy.splice(0, 2).join("")}-${copy.splice(0, 2).join("")}`
    }
    case "Select": return value === "" ? "0" : value
    case "Percent": {
      if (value === "" || value == null) {
        return "";
      }
      const num = typeof value === "number" ? value : parseFloat(value);
      if (Number.isNaN(num)) {
        return "";
      }
      // Round to the precision (in displayed-percent space) to avoid floating-point noise like 49.99999.
      const step = setting.precision ?? 1;
      const displayed = num * 100;
      const rounded = Math.round(displayed / step) * step;
      return rounded.toString();
    }
    case "Float":
    case "Boolean":
    case "DateTime":
    case "Year":
    default: return value?.toString() || value
  }
}

function SettingsInput(props: { setting: SingleSetting, value: any, disabled?: boolean, onChange: (value) => any }) {
  const lang: "da" | "en" = Localization.getLanguage() as "da" | "en";

  const value = valueToString(props.setting, props.value)

  function onChange(ev) {
    props.onChange(ev.target.value)
  }

  let si = props.setting;
  switch (si.type) {
    case "String": return <input disabled={props.disabled} value={value} onChange={onChange}></input>
    case "TextArea": return <textarea disabled={props.disabled} value={value} onChange={onChange} />
    case "Select": return <select disabled={props.disabled} value={value} onChange={onChange}>{si.options.map((a) => <option key={a.value} value={a.value} >{a[lang]}</option>)}</select>
    case "Integer": return <input disabled={props.disabled} value={value} onChange={onChange} type="number" max={si.max} min={si.min} />
    case "Float": return <input disabled={props.disabled} value={value} onChange={onChange} type="number" step={si.precision ?? 0.1} max={si.max} min={si.min} />
    case "Percent": return <input disabled={props.disabled} value={value} onChange={onChange} type="number" step={si.precision ?? 1} max={si.max ?? 100} min={si.min ?? 0} />
    case "Boolean": return <select disabled={props.disabled} value={value} onChange={onChange}><option value={"true"} >{si.true[lang]}</option><option value={"false"} >{si.false[lang]}</option></select>
    case "TimeADay": return <input disabled={props.disabled} value={value} onChange={onChange} type="time" />
    case "Date": return <input disabled={props.disabled} value={value} onChange={onChange} type="date" />
    case "DateTime": return <input disabled={props.disabled} value={value} onChange={onChange} type="datetime-local" />
    case "Year": return <input disabled={props.disabled} value={value} onChange={onChange} type="number" min={2000} max={2900} />
    case "ExternalEditor": { return <ExternalEditor disabled={props.disabled} value={props.value} onChange={props.onChange} windowId={si.windowId} default={si.default} /> }
    default: return <>{value}</>
  }
  return <></>
}

function flattenSettings(settings: { [key: string]: unknown }) {
  let newSettings: { [key: string]: unknown } = {}
  Object.keys(settings).forEach((key) => {
    if (typeof settings[key] === "object" && !Array.isArray(settings[key]) && !(settings[key] instanceof Date)) {
      Object.keys(settings[key] as object).forEach((nestedKey) => {
        newSettings[key + "." + nestedKey] = (settings[key] as object)[nestedKey]
      })
    } else {
      newSettings[key] = settings[key]
    }
  })
  return newSettings
}

function handleNestedSettings(keys: string[], object: { [key: string]: any }) {
  let newObject: any = {}
  for (let key of keys) {
    if (key in object) {
      newObject[key] = object[key]
    }
    Object.keys(object).filter((a) => a.startsWith(key + ".")).forEach((a) => {
      newObject[key] = {
        ...(newObject[key]),
        [a.split(key + ".").at(-1) as string]: object[a]
      }
    });
  };
  Object.keys(newObject).filter((_a) => {
    // FIXME: This return void
    // !(a in newObject) || !(a.split(".").splice(0, -1).join(".") in newObject)
  }).forEach((a) => {
    newObject[a] = object[a]
  })
  return newObject
}

function removeUndefined(node) {
  let newNode = node
  let keys = Object.keys(newNode).filter((a) => {
    // Remove undefined
    return newNode[a] != undefined
  }).filter((a) => {
    // Remove resetted
    return !(newNode[a].reset || false)
  }).filter((a) => {
    // Remove disabled
    return !(newNode[a].disabled || false)
  })
  let result = {}
  keys.forEach((a) => {
    result = { ...result, [a]: newNode[a].value }
  })
  return result
}

function format2Digits(number: number) {
  return number.toLocaleString('dk-da', { minimumIntegerDigits: 2 })
}

function ExternalEditor(props: { disabled?: boolean, value?: any, onChange?: (value: any) => void, default?: any, windowId: WindowId }) {
  const { dispatch } = useWindowContext()

  return (
    <button disabled={props.disabled} className="external-editor-button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        dispatch(openWindow(props.windowId,
          {
            value: props.value,
            default: props.default,
            onChange: (value) => {
              if (props.onChange) {
                props.onChange(value)
              }
            }

          })
        )
      }}
    >
      Open Editor
    </button>
  )
}